// Game orchestration: owns the simulation and the renderers, runs a fixed
// timestep loop, and translates input into mining, building and combat. Also
// handles pause/settings, audio cues, underwater tint and the F3 debug overlay.

import { Block, blockDef, isSolid } from './sim/blocks';
import { attackDamageOf, miningMultiplier } from './sim/items';
import { config } from './sim/config';
import { PLAYER_HEIGHT, PLAYER_WIDTH, REACH, TICK_DT } from './sim/constants';
import { skyState } from './sim/daynight';
import { Inventory } from './sim/inventory';
import { Vec3, vec3 } from './sim/math';
import { MobManager, Zombie } from './sim/mobs';
import { Player } from './sim/player';
import { raycast, RayHit } from './sim/raycast';
import { World } from './sim/world';
import { ChunkRenderer } from './render/chunkRenderer';
import { BlockHighlight } from './render/highlight';
import { Hud } from './render/hud';
import { MobRenderer } from './render/mobRenderer';
import { Renderer } from './render/renderer';
import { AudioManager } from './render/audio';
import { Input } from './input';
import { CraftingScreen, InventoryScreen, PauseMenu, SettingsPanel } from './ui';
import { SavedWorld } from './persist';

const ENTITY_REACH = 3.5;
const ATTACK_COOLDOWN = 0.4;

export interface GameOptions {
  name: string;
  audio: AudioManager;
  saved?: SavedWorld | null;
  seed?: number;
  onQuit: () => void;
}

export class Game {
  readonly name: string;
  private world: World;
  private player: Player;
  private inventory = new Inventory();
  private mobs = new MobManager();

  private renderer: Renderer;
  private chunks: ChunkRenderer;
  private mobRenderer: MobRenderer;
  private highlight: BlockHighlight;
  private hud: Hud;
  private input: Input;
  private audio: AudioManager;
  private pauseMenu: PauseMenu;
  private settings: SettingsPanel;
  private inventoryScreen: InventoryScreen;
  private inventoryOpen = false;
  private craftingScreen: CraftingScreen;
  private craftingOpen = false;
  private onQuit: () => void;

  private miningTarget: Vec3 | null = null;
  private miningProgress = 0;
  private attackTimer = 0;
  private stepTimer = 0;
  private lastHealth: number;
  private wasDead = false;
  private groanTimers = new Map<number, number>();

  private paused = false;
  private quitting = false;
  private debug = false;
  private running = false;
  private rafId = 0;

  private accumulator = 0;
  private lastTime = 0;
  private frameCount = 0;
  private fpsTimer = 0;
  private fps = 0;
  private frameMs = 0;

  constructor(canvas: HTMLCanvasElement, opts: GameOptions) {
    this.name = opts.name;
    this.audio = opts.audio;
    this.onQuit = opts.onQuit;

    const seed = opts.saved ? opts.saved.seed : (opts.seed ?? (Math.random() * 2 ** 31) | 0);
    this.world = new World(seed);
    this.world.ensureChunk(0, 0);
    const spawnY = this.world.surfaceSpawnY(0, 0);
    this.player = new Player(vec3(0.5, spawnY, 0.5));

    if (opts.saved) this.restore(opts.saved);
    this.lastHealth = this.player.health;

    this.renderer = new Renderer(canvas);
    this.chunks = new ChunkRenderer(this.renderer.scene, this.world);
    this.mobRenderer = new MobRenderer(this.renderer.scene);
    this.highlight = new BlockHighlight(this.renderer.scene);
    this.hud = new Hud(document.body);
    this.hud.setRespawnHandler(() => this.player.respawn());

    this.settings = new SettingsPanel(
      () => this.renderer.applyConfig(),
      () => {
        this.settings.hide();
        if (this.paused) this.pauseMenu.show();
      },
    );
    this.pauseMenu = new PauseMenu({
      onResume: () => {
        this.pauseMenu.hide();
        this.input.requestLock();
      },
      onSettings: () => {
        this.pauseMenu.hide();
        this.settings.show();
      },
      onQuit: () => {
        this.quitting = true;
        this.pauseMenu.hide();
        this.onQuit();
      },
    });

    this.inventoryScreen = new InventoryScreen(this.inventory, () => this.closeInventory());
    this.craftingScreen = new CraftingScreen(this.inventory, () => this.closeCrafting());

    this.input = new Input(canvas);
    this.input.onSelectSlot = (i) => this.inventory.select(i);
    this.input.onScroll = (d) => this.inventory.scroll(d);
    this.input.onDebugToggle = () => (this.debug = !this.debug);
    this.input.onInventoryToggle = () => this.toggleInventory();
    this.input.onLockChange = (locked) => this.onLockChange(locked);
  }

  private toggleInventory(): void {
    if (this.player.dead) return;
    if (this.craftingOpen) this.closeCrafting();
    else if (this.inventoryOpen) this.closeInventory();
    else if (this.input.locked) this.openInventory();
  }

  private openInventory(): void {
    this.inventoryOpen = true;
    this.inventoryScreen.show();
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private closeInventory(): void {
    this.inventoryOpen = false;
    this.inventoryScreen.hide();
    this.input.requestLock();
  }

  private openCrafting(): void {
    this.craftingOpen = true;
    this.craftingScreen.show();
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private closeCrafting(): void {
    this.craftingOpen = false;
    this.craftingScreen.hide();
    this.input.requestLock();
  }

  private restore(saved: SavedWorld): void {
    this.world.time = saved.time;
    for (const [key, blocks] of Object.entries(saved.chunks)) {
      const [cx, cz] = key.split(',').map(Number);
      this.world.applySavedChunk(cx, cz, new Uint8Array(blocks));
    }
    const p = saved.player;
    this.player.pos = vec3(p.x, p.y, p.z);
    this.player.spawn = vec3(p.x, p.y, p.z);
    this.player.yaw = p.yaw;
    this.player.pitch = p.pitch;
    this.player.health = p.health;
    for (let i = 0; i < this.inventory.slots.length; i++) {
      const s = p.slots[i];
      // Back-compat: older saves stored the id under `block` instead of `item`.
      const id = s ? (s.item ?? (s as unknown as { block: number }).block) : null;
      this.inventory.slots[i] = s && id != null ? { item: id, count: s.count } : null;
    }
    this.inventory.selected = p.selected;
  }

  getSaveData(): SavedWorld {
    const chunks: Record<string, Uint8Array> = {};
    for (const c of this.world.modifiedChunks()) {
      chunks[`${c.cx},${c.cz}`] = new Uint8Array(c.blocks);
    }
    return {
      name: this.name,
      seed: this.world.seed,
      time: this.world.time,
      player: {
        x: this.player.pos.x,
        y: this.player.pos.y,
        z: this.player.pos.z,
        yaw: this.player.yaw,
        pitch: this.player.pitch,
        health: this.player.health,
        selected: this.inventory.selected,
        slots: this.inventory.slots.map((s) => (s ? { item: s.item, count: s.count } : null)),
      },
      chunks,
      savedAt: Date.now(),
    };
  }

  start(): void {
    this.running = true;
    this.lastTime = performance.now();
    this.rafId = requestAnimationFrame((t) => this.frame(t));
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    if (document.pointerLockElement) document.exitPointerLock();
    this.input.dispose();
    this.chunks.dispose();
    this.mobRenderer.clear();
    this.renderer.dispose();
    this.hud.dispose();
    this.pauseMenu.dispose();
    this.settings.dispose();
    this.inventoryScreen.dispose();
    this.craftingScreen.dispose();
  }

  private onLockChange(locked: boolean): void {
    if (locked) {
      this.audio.resume();
      this.setPaused(false);
    } else if (!this.player.dead && !this.quitting && !this.inventoryOpen && !this.craftingOpen) {
      // Pointer released for a reason other than a menu → pause.
      this.setPaused(true);
    }
  }

  private setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) {
      this.pauseMenu.show();
    } else {
      this.pauseMenu.hide();
      this.settings.hide();
    }
  }

  private frame(now: number): void {
    if (!this.running) return;
    const dtReal = Math.min((now - this.lastTime) / 1000, 0.1);
    this.lastTime = now;
    const t0 = performance.now();

    if (!this.paused) {
      this.applyMouseLook();
      this.accumulator += dtReal;
      let steps = 0;
      while (this.accumulator >= TICK_DT && steps < 5) {
        this.tick(TICK_DT);
        this.accumulator -= TICK_DT;
        steps++;
      }
    }

    this.render(dtReal);
    this.frameMs = performance.now() - t0;
    this.rafId = requestAnimationFrame((tt) => this.frame(tt));
  }

  private applyMouseLook(): void {
    const { dx, dy } = this.input.consumeMouseDelta();
    if (dx === 0 && dy === 0) return;
    this.player.yaw -= dx * config.mouseSensitivity;
    this.player.pitch -= dy * config.mouseSensitivity;
    const limit = Math.PI / 2 - 0.01;
    this.player.pitch = Math.max(-limit, Math.min(limit, this.player.pitch));
  }

  private tick(dt: number): void {
    this.world.time += dt;
    const canAct = this.input.locked && !this.player.dead;
    const move = canAct
      ? this.input.movementState()
      : { forward: false, back: false, left: false, right: false, jump: false };
    this.player.update(this.world, move, dt);
    this.mobs.update(this.world, this.player, dt);
    if (this.attackTimer > 0) this.attackTimer -= dt;
    this.updateMobGroans(dt);

    if (canAct) {
      this.handleInteraction(dt);
      this.handleFootsteps(dt);
    }

    // Damage + death audio/feedback.
    if (this.player.health < this.lastHealth) this.audio.play('hurt');
    this.lastHealth = this.player.health;
    if (this.player.dead && !this.wasDead) {
      if (document.pointerLockElement) document.exitPointerLock();
    }
    this.wasDead = this.player.dead;
  }

  /** Occasional positional zombie groans so you can hear where they are. */
  private updateMobGroans(dt: number): void {
    const alive = new Set<number>();
    for (const mob of this.mobs.mobs) {
      alive.add(mob.id);
      let t = this.groanTimers.get(mob.id) ?? 2 + Math.random() * 4;
      t -= dt;
      if (t <= 0) {
        this.audio.playPositional('groan', { x: mob.pos.x, y: mob.pos.y + 1.5, z: mob.pos.z });
        t = 3 + Math.random() * 5;
      }
      this.groanTimers.set(mob.id, t);
    }
    for (const id of [...this.groanTimers.keys()]) {
      if (!alive.has(id)) this.groanTimers.delete(id);
    }
  }

  private handleFootsteps(dt: number): void {
    const moving = this.player.onGround && Math.hypot(this.player.vel.x, this.player.vel.z) > 0.5;
    if (!moving) {
      this.stepTimer = 0;
      return;
    }
    this.stepTimer -= dt;
    if (this.stepTimer <= 0) {
      this.stepTimer = 0.34;
      this.audio.play('step');
    }
  }

  private handleInteraction(dt: number): void {
    const hit = this.currentHit();
    const targetMob = this.mobUnderCrosshair();

    const leftClicks = this.input.consumeLeftClicks();
    if (targetMob && leftClicks > 0 && this.attackTimer <= 0) {
      // Damage comes from the held item's registry entry (hand=1, swords more).
      const damage = attackDamageOf(this.inventory.selectedItem());
      const killed = targetMob.hurt(damage, this.player.pos.x, this.player.pos.z);
      const at = { x: targetMob.pos.x, y: targetMob.pos.y + 1, z: targetMob.pos.z };
      this.audio.playPositional('mobhit', at);
      if (killed) this.audio.playPositional('groan', at);
      this.renderer.swingHand();
      this.attackTimer = ATTACK_COOLDOWN;
    }

    if (!targetMob && this.input.leftDown && hit) {
      this.progressMining(hit.block, dt);
    } else {
      this.resetMining();
    }

    const rightClicks = this.input.consumeRightClicks();
    if (rightClicks > 0 && hit) {
      // Right-clicking a placed crafting table opens the crafting popup.
      if (this.world.getBlock(hit.block.x, hit.block.y, hit.block.z) === Block.CraftingTable) {
        this.openCrafting();
      } else {
        this.tryPlace(hit);
      }
    }
  }

  private progressMining(block: Vec3, dt: number): void {
    if (
      !this.miningTarget ||
      this.miningTarget.x !== block.x ||
      this.miningTarget.y !== block.y ||
      this.miningTarget.z !== block.z
    ) {
      this.miningTarget = { ...block };
      this.miningProgress = 0;
    }
    const id = this.world.getBlock(block.x, block.y, block.z);
    const hardness = blockDef(id).hardness;
    if (!isFinite(hardness)) return;
    // Mining speed = hardness ÷ tool multiplier (data-driven; hand = 1).
    const mult = miningMultiplier(this.inventory.selectedItem(), id);
    this.miningProgress += (dt * mult) / hardness;
    if (this.miningProgress >= 1) {
      this.breakBlock(block, id);
      this.resetMining();
    }
  }

  private breakBlock(block: Vec3, id: Block): void {
    this.world.setBlock(block.x, block.y, block.z, Block.Air);
    this.chunks.remeshAround(block.x, block.z);
    this.audio.play('break');
    this.renderer.swingHand();
    if (blockDef(id).placeable) {
      this.inventory.add(id, 1);
      this.audio.play('pickup');
    }
  }

  private resetMining(): void {
    this.miningTarget = null;
    this.miningProgress = 0;
  }

  private tryPlace(hit: RayHit): void {
    const cell = hit.place;
    if (isSolid(this.world.getBlock(cell.x, cell.y, cell.z))) return;
    if (this.blockIntersectsPlayer(cell)) return;
    const block = this.inventory.selectedBlock();
    if (block === null) return;
    if (this.world.setBlock(cell.x, cell.y, cell.z, block)) {
      this.inventory.consumeSelected();
      this.chunks.remeshAround(cell.x, cell.z);
      this.audio.play('place');
      this.renderer.swingHand();
    }
  }

  private blockIntersectsPlayer(cell: Vec3): boolean {
    const half = PLAYER_WIDTH / 2;
    return (
      cell.x + 1 > this.player.pos.x - half &&
      cell.x < this.player.pos.x + half &&
      cell.y + 1 > this.player.pos.y &&
      cell.y < this.player.pos.y + PLAYER_HEIGHT &&
      cell.z + 1 > this.player.pos.z - half &&
      cell.z < this.player.pos.z + half
    );
  }

  private currentHit(): RayHit | null {
    return raycast(this.world, this.player.eye(), this.player.lookDir(), REACH);
  }

  private mobUnderCrosshair(): Zombie | null {
    const eye = this.player.eye();
    const dir = this.player.lookDir();
    let best: Zombie | null = null;
    let bestDist = ENTITY_REACH;
    for (const mob of this.mobs.mobs) {
      const cx = mob.pos.x - eye.x;
      const cy = mob.pos.y + mob.height / 2 - eye.y;
      const cz = mob.pos.z - eye.z;
      const dist = Math.hypot(cx, cy, cz);
      if (dist > ENTITY_REACH) continue;
      const dot = (cx * dir.x + cy * dir.y + cz * dir.z) / (dist || 1);
      if (dot > 0.95 && dist < bestDist) {
        best = mob;
        bestDist = dist;
      }
    }
    return best;
  }

  private isEyeUnderwater(): boolean {
    const eye = this.player.eye();
    return (
      this.world.getBlock(Math.floor(eye.x), Math.floor(eye.y), Math.floor(eye.z)) === Block.Water
    );
  }

  private render(dtReal: number): void {
    const sky = skyState(this.world.time);
    this.renderer.updateCamera(this.player.eye(), this.player.yaw, this.player.pitch);
    this.renderer.updateSky(sky, this.player.pos);
    this.audio.updateListener(this.player.eye(), this.player.lookDir());
    this.chunks.update(this.player.pos.x, this.player.pos.z);
    this.mobRenderer.sync(this.mobs);

    const hit = this.currentHit();
    if (hit && !this.player.dead && this.input.locked) this.highlight.show(hit.block);
    else this.highlight.hide();

    this.renderer.setHeldItem(this.inventory.selectedItem());
    this.renderer.updateHeld(dtReal);
    this.renderer.render();
    this.updateHud(dtReal, sky);
  }

  private updateHud(dtReal: number, sky: ReturnType<typeof skyState>): void {
    this.frameCount++;
    this.fpsTimer += dtReal;
    if (this.fpsTimer >= 0.5) {
      this.fps = Math.round(this.frameCount / this.fpsTimer);
      this.frameCount = 0;
      this.fpsTimer = 0;
    }
    const hour = Math.floor(sky.fraction * 24);
    const timeLabel = `${sky.isNight ? '🌙' : '☀'} ${String(hour).padStart(2, '0')}:00`;

    let debug: string | null = null;
    if (this.debug) {
      const p = this.player.pos;
      debug =
        `fps ${this.fps}  frame ${this.frameMs.toFixed(1)}ms\n` +
        `draw calls ${this.renderer.drawCalls}  tris ${(this.renderer.triangles / 1000).toFixed(1)}k\n` +
        `xyz ${p.x.toFixed(1)} ${p.y.toFixed(1)} ${p.z.toFixed(1)}\n` +
        `chunks ${this.chunks.loadedCount}  mobs ${this.mobs.mobs.length}\n` +
        `view ${config.viewDistanceChunks}  day ${(sky.fraction * 100).toFixed(0)}%`;
    }

    this.hud.update({
      fps: this.fps,
      inventory: this.inventory,
      health: this.player.health,
      dead: this.player.dead,
      kills: this.mobs.kills,
      timeLabel,
      chunks: this.chunks.loadedCount,
      mobs: this.mobs.mobs.length,
      miningProgress: this.miningTarget ? this.miningProgress : null,
      underwater: this.isEyeUnderwater(),
      debug,
    });
  }
}
